import get from 'lodash/get';
import {useEffect,useState} from 'react';
import {useNavigate,useParams} from 'react-router-dom';

import {useGetPatientsQuery} from '../../redux/services/patientApi';
import {useGetProvidersQuery} from '../../redux/services/providerApi';
import {
  useGetMessagesQuery,
  useCreateMessageMutation,
  useUpdateMessageMutation
} from '../../redux/services/messageApi';
import useError from '../../hooks/useError';
import useUser from '../../hooks/useUser';
import AppDashboard from '../AppDashboard';
import ViewMessageThreadScreen from '../../components/messages/ViewMessageThreadScreen';

const handleSubmit = ({
  createMessage,
  setError,
  messagesQuery,
  setSubmitting,
  setText
}) => (
  async data => {
    setSubmitting(true);
    const res = await createMessage(data);
    setSubmitting(false);
    if (res.error) {
      setError({message: 'There was an error sending your message!'});
      return res;
    }
    setText('');
    const promise = await messagesQuery.refetch();
    return res;
  }
);

const markRead = async (messages, updateMessage) => {
  return await Promise.all(messages.map(m =>
    updateMessage({id: m.id, body: {is_read: true}})
  ));
};

const useMarkRead = ({messagesQuery, user, updateMessage, markedUnread}) => {
  const unreadMessages = (get(messagesQuery, 'data.data', []) || []).filter(m =>
    m.user_to.id === user?.id
    && m.is_read === false
  );
  const isLoaded = messagesQuery.isSuccess;
  const unreadLength = unreadMessages?.length;
  useEffect(() => {
    if (!markedUnread && unreadLength && isLoaded) markRead(unreadMessages, updateMessage);
  }, [isLoaded, markedUnread]);
};

const handleMarkUnread = ({updateMessage, setMarkedUnread}) => async (messageId) => {
  const res = await updateMessage({id: messageId, body: {is_read: false}});
  setMarkedUnread(true);
  return res;
};

const ViewMessageThreadPage = props => {
  const [isSubmitting, setSubmitting] = useState(false);
  const [text, setText] = useState('');
  const [markedUnread, setMarkedUnread] = useState(false);
  const {id: rawId} = useParams();
  const id = parseInt(rawId, 10);
  const navigate = useNavigate();
  const errors = useError();
  const user = useUser();
  const isProvider = get(user, 'is_provider');
  const [createMessage] = useCreateMessageMutation();
  const [updateMessage] = useUpdateMessageMutation();
  const patientQuery = useGetPatientsQuery({id, hide_details: true}, {skip: !id || !isProvider});
  const providerQuery = useGetProvidersQuery({id}, {skip: !id || isProvider});
  const patient = (get(patientQuery, 'data.data') || []).find(p => p.id === id);
  const provider = get(providerQuery, 'data[0]') || null;
  const otherUserId = (
    user?.is_provider
    ? patient?.user.id
    : provider?.user.id
  );
  const messagesQuery = useGetMessagesQuery(
    {other_user_id: otherUserId, sort: 'date_sent'},
    {skip: !otherUserId, pollingInterval: 30000}
  );
  useMarkRead({messagesQuery, user, updateMessage, markedUnread});
  const otherUser = (
    isProvider
    ? patient
    : (get(providerQuery, 'data') || []).find(p => parseInt(id, 10) === p.id)
  );
  const childProps = {
    ...props,
    otherUserId,
    otherUser,
    user,
    isProvider,
    text,
    isSubmitting,
    onChange: e => setText(e.target.value),
    messages: (get(messagesQuery, 'data.data') || []),
    loading: patientQuery.isLoading || messagesQuery.isLoading,
    onMarkUnread: handleMarkUnread({updateMessage, setMarkedUnread}),
    onSendMessage: handleSubmit({
      createMessage,
      messagesQuery,
      setError: errors.create,
      setSubmitting,
      setText,
    }),
    onBack: () => (
      user.is_patient
      ? navigate('/clients/messages')
      : navigate('/providers/messages')
    ),
    onViewPatientProfile: () => navigate(`/providers/patients/view/${id}`)
  };
  return (
    <AppDashboard
      title={`Messages`}
      loading={childProps.loading}
      hasNoData={!childProps.loading && !childProps.messages}
      onBack={childProps.onBack}
    >
      <ViewMessageThreadScreen {...childProps} />
    </AppDashboard>
  );
};

export default ViewMessageThreadPage;
